import { Navigate, Route, Routes } from 'react-router'
import type { JSX } from 'react'
import { RequireAnonymous, RequireAuth } from '@/auth/guards'
import { RequireTenant } from '@/tenant/RequireTenant'
import { AppShell } from '@/shell/AppShell'
import { AcceptInvitation } from '@/pages/public/AcceptInvitation'
import { CheckEmail } from '@/pages/public/CheckEmail'
import { ForgotPassword } from '@/pages/public/ForgotPassword'
import { Login } from '@/pages/public/Login'
import { OutreachUnsubscribe } from '@/pages/public/OutreachUnsubscribe'
import { ResetPassword } from '@/pages/public/ResetPassword'
import { Signup } from '@/pages/public/Signup'
import { Unsubscribe } from '@/pages/public/Unsubscribe'
import { VerifyEmail } from '@/pages/public/VerifyEmail'
import { VerifyEmailChange } from '@/pages/public/VerifyEmailChange'
import { VerifyMobile } from '@/pages/public/VerifyMobile'
import { BrandVoiceStep } from '@/onboarding/BrandVoiceStep'
import { Finished } from '@/onboarding/Finished'
import { KeywordsStep } from '@/onboarding/KeywordsStep'
import { LinkedInStep } from '@/onboarding/LinkedInStep'
import { OnboardingBoundary } from '@/onboarding/OnboardingBoundary'
import { OnboardingIndex } from '@/onboarding/OnboardingIndex'
import { Resume } from '@/onboarding/Resume'
import { SearchConsoleStep } from '@/onboarding/SearchConsoleStep'
import { SenderStep } from '@/onboarding/SenderStep'
import { SiteStep } from '@/onboarding/SiteStep'
import { WizardLayout } from '@/onboarding/WizardLayout'
import { BillingPage } from '@/pages/billing/BillingPage'
import { CheckoutReturn } from '@/pages/billing/CheckoutReturn'
import { PlansPage } from '@/pages/billing/PlansPage'
import { ModuleOverview } from '@/pages/ModuleOverview'
import { NotFound } from '@/pages/NotFound'
import { Organisations } from '@/pages/Organisations'
import { OrganisationPanel } from '@/pages/settings/OrganisationPanel'
import { NotificationsPanel } from '@/pages/settings/NotificationsPanel'
import { SessionsPanel } from '@/pages/settings/SessionsPanel'
import { SettingsLayout } from '@/pages/settings/SettingsLayout'
import { DangerZonePanel } from '@/pages/settings/DangerZonePanel'
import { TeamPanel } from '@/pages/settings/TeamPanel'

/**
 * The route table.
 *
 * Five bands, and which band a route is in is a security statement rather than
 * a layout one:
 *
 *  1. **Token pages** — no guard at all. Each is opened from a link in an email
 *     by someone who may not be signed in, on a device that may not be theirs.
 *     `/accept-invitation` is here even though accepting needs a session,
 *     because the page has to *say* that rather than bounce and lose the token.
 *  2. **Anonymous-only** — sign in and sign up, which redirect a signed-in user
 *     onward rather than showing them a form they have no use for.
 *  3. **Signed in, no organisation needed** — the organisation picker, and the
 *     two confirmation screens a brand-new account lands on. `/verify-mobile`
 *     and `/check-email` are here rather than in band 2 because signup leaves a
 *     session: they are reached *while* signed in, and a `RequireAnonymous`
 *     guard would redirect straight past them.
 *  4. **The onboarding wizard** — needs an organisation, but not the shell. A
 *     tenant part-way through setup has nothing for a sidebar to describe.
 *  5. **Inside the shell** — everything that needs an organisation selected on
 *     the session.
 *
 * Declarative `<Routes>` rather than a data router: nothing here loads through
 * a route loader, and `MemoryRouter` makes the whole table testable without a
 * browser history.
 */
export function AppRoutes(): JSX.Element {
  return (
    <Routes>
      {/* 1 — links already in the wild */}
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/verify-email-change" element={<VerifyEmailChange />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/accept-invitation" element={<AcceptInvitation />} />
      <Route path="/check-email" element={<CheckEmail />} />
      <Route path="/verify-mobile" element={<VerifyMobile />} />
      <Route path="/notifications/unsubscribe" element={<Unsubscribe />} />
      {/* S-B12. The §8 one-click link, and the only public page whose reader
          has no relationship with AugRank — they were contacted by a tenant. */}
      <Route path="/outreach/unsubscribe" element={<OutreachUnsubscribe />} />

      {/* 2 */}
      <Route element={<RequireAnonymous />}>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
      </Route>

      {/* 3, 4 and 5 */}
      <Route element={<RequireAuth />}>
        <Route path="/organisations" element={<Organisations />} />

        <Route element={<RequireTenant />}>
          <Route element={<OnboardingBoundary />}>
            <Route path="/onboarding" element={<WizardLayout />}>
              <Route index element={<OnboardingIndex />} />
              <Route path="site" element={<SiteStep />} />
              <Route path="search-console" element={<SearchConsoleStep />} />
              <Route path="linkedin" element={<LinkedInStep />} />
              <Route path="sender" element={<SenderStep />} />
              <Route path="keywords" element={<KeywordsStep />} />
              <Route path="brand-voice" element={<BrandVoiceStep />} />
              <Route path="done" element={<Finished />} />
              <Route path="resume" element={<Resume />} />
            </Route>

            <Route element={<AppShell />}>
              <Route index element={<Navigate to="/seo" replace />} />
              <Route path="/seo" element={<ModuleOverview module="seo" />} />
              <Route path="/outreach" element={<ModuleOverview module="outreach" />} />
              {/* S-B6. Billing is its own surface rather than a ninth settings
                  panel: S-D3 approved eight and `admin.html` draws billing as
                  standalone screens. Reached from the account menu, which is
                  where every SaaS puts it and which already exists. */}
              <Route path="/billing" element={<BillingPage />} />
              <Route path="/billing/plans" element={<PlansPage />} />
              <Route path="/billing/return" element={<CheckoutReturn />} />
              <Route path="/settings" element={<SettingsLayout />}>
                <Route index element={<Navigate to="/settings/organisation" replace />} />
                <Route path="organisation" element={<OrganisationPanel />} />
                <Route path="team" element={<TeamPanel />} />
                <Route path="notifications" element={<NotificationsPanel />} />
                <Route path="sessions" element={<SessionsPanel />} />
                <Route path="danger-zone" element={<DangerZonePanel />} />
              </Route>
            </Route>
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  )
}
